import {
  BENCHMARKS,
  COLUMN_TYPES,
  HEADLINE_BENCHMARK,
  PARAM_TYPES,
  UNCACHED_BENCHMARK,
} from "@saucer/core";
import { CodeBlock, type Lang } from "./code-block.js";

/**
 * The managed-database documentation, as data rather than as a page.
 *
 * It lives here because it is rendered twice: on the public docs page at
 * `/managed/docs`, and inside the workspace itself, where someone who is halfway
 * through building a pipe should not have to leave the thing they are building
 * to find out what `{{Int64(limit, 10)}}` means. Two copies of this prose would
 * drift within a release — the same reason the benchmark numbers below are read
 * from `@saucer/core` instead of being retyped.
 *
 * Ordered the way the work actually goes — create, ingest, query, publish,
 * secure, watch — rather than by API surface, because the first question is
 * always "how do I get from nothing to a URL".
 */

/**
 * The slowest measured shape, read from the benchmarks rather than retyped —
 * a number written twice is a number that will disagree with itself.
 */
const SLOWEST_P50 = Math.max(...BENCHMARKS.map((run) => run.p50));

export interface Section {
  id: string;
  title: string;
  body: React.ReactNode;
}

/** Same block the product page uses, so the two read as one set of docs. */
function Code({ children, lang = "bash" }: { children: string; lang?: Lang }) {
  return <CodeBlock code={children} lang={lang} />;
}

export const SECTIONS: Section[] = [
  {
    id: "start",
    title: "Getting started",
    body: (
      <>
        <p>
          Create a workspace from the dashboard. A ClickHouse workspace gets you data sources,
          pipes and endpoints; a Postgres database is just Postgres, reachable with any client.
        </p>
        <p>
          The password is shown <strong>once</strong>, at creation. It is stored only as a hash,
          here and on the machine that serves it, so there is no way to show it again — if you
          lose it, rotate for a new one. Rotation takes effect immediately.
        </p>
        <Code lang="bash">{`# Postgres, straight away
psql "postgresql://you_main:PASSWORD@pg.saucer.sh:5432/you_main?sslmode=require"

# ClickHouse over its native protocol
clickhouse-client --host ch.saucer.sh --port 9440 --secure \\
  --user you_events --password PASSWORD --database you_events`}</Code>
        <p className="mdocs__note">
          TLS is mandatory on both. A Postgres client connecting with{" "}
          <code>sslmode=disable</code> is refused with a message telling it so, rather than
          having the socket dropped.
        </p>
      </>
    ),
  },
  {
    id: "quickstart",
    title: "Quickstart: nothing to a URL",
    body: (
      <>
        <p>
          The whole product in five calls. Everything after this section is detail on one of
          them. <code>$SC</code> is an account token from Settings; <code>$WS</code> is the
          workspace id in the URL when you open it.
        </p>
        <Code lang="bash">{`# 1 — a data source. The sorting key is the decision that matters.
curl -X POST "https://saucer.sh/api/workspaces/$WS/datasources" \\
  -H "Authorization: Bearer $SC" -H "Content-Type: application/json" \\
  -d '{
    "name": "hits",
    "columns": [
      {"name": "timestamp", "type": "DateTime"},
      {"name": "url",       "type": "String"},
      {"name": "country",   "type": "String"}
    ],
    "sortingKey": ["country", "url", "timestamp"],
    "partitionBy": "toYYYYMM(timestamp)",
    "ttl": "timestamp + INTERVAL 90 DAY"
  }'

# 2 — a pipe, published in the same call. endpointNode is what makes it live.
curl -X PUT "https://saucer.sh/api/workspaces/$WS/pipes/top_urls" \\
  -H "Authorization: Bearer $SC" -H "Content-Type: application/json" \\
  -d '{
    "nodes": [
      {"name": "recent",   "sql": "SELECT * FROM hits WHERE timestamp >= now() - toIntervalDay(7)"},
      {"name": "endpoint", "sql": "SELECT url, count() AS hits FROM recent WHERE country = {{String(country, \\'US\\')}} GROUP BY url ORDER BY hits DESC LIMIT {{Int64(limit, 10)}}"}
    ],
    "endpointNode": "endpoint",
    "cacheSeconds": 10,
    "maxThreads": 2
  }'

# 3 — a token. Mint this one in the dashboard: token creation refuses
#     account tokens on purpose (see Control API → Tokens).

# 4 — ingest. NDJSON, one object per row.
curl -X POST "https://api.saucer.sh/v0/events?name=hits" \\
  -H "Authorization: Bearer $APPEND_TOKEN" \\
  --data-binary '{"timestamp":"2026-08-23 10:00:00","url":"/pricing","country":"DE"}'

# 5 — read it back.
curl "https://api.saucer.sh/v0/pipes/top_urls.json?country=DE&limit=5" \\
  -H "Authorization: Bearer $READ_TOKEN"`}</Code>
        <p className="mdocs__note">
          Two hostnames, and the split is deliberate. <code>saucer.sh/api</code> is the control
          plane on Cloudflare — it creates and publishes things. <code>api.saucer.sh</code> is
          the daemon beside the database — it ingests and answers. A published endpoint never
          touches Cloudflare, which is the reason it can answer in{" "}
          {HEADLINE_BENCHMARK.p50} ms.
        </p>
      </>
    ),
  },
  {
    id: "datasources",
    title: "Data sources",
    body: (
      <>
        <p>
          A data source is one MergeTree table plus the two decisions that determine whether
          queries over it are fast.
        </p>
        <p>
          <strong>The sorting key is the one that matters.</strong> It is the physical order of
          the data, so a filter on its leading columns skips whole granules instead of reading
          them. A query that filters on something not in the key reads everything — that is the
          difference between {UNCACHED_BENCHMARK.p50} ms and {SLOWEST_P50} ms on the same
          table. It cannot be changed after the table exists, so put the columns you filter on
          most first.
        </p>
        <p>
          <strong>The partition is coarser</strong>, usually a month. It exists so that dropping
          old data is a file delete rather than a mutation.
        </p>
        <Code lang="bash">{`columns      timestamp DateTime, url String, country String, revenue Float64
sorting key  country, url, timestamp
partition    toYYYYMM(timestamp)
ttl          timestamp + INTERVAL 90 DAY`}</Code>
        <p>Column types available:</p>
        <p className="mdocs__chips">
          {COLUMN_TYPES.map((type) => (
            <span key={type} className="md-chip mono">
              {type}
            </span>
          ))}
        </p>
        <p className="mdocs__note">
          Partition and TTL accept a small, checked grammar — functions of your own columns, and
          nothing that reads data. Anything else is refused rather than sanitised. A nullable
          column cannot be part of a sorting key.
        </p>
      </>
    ),
  },
  {
    id: "ingest",
    title: "Ingesting events",
    body: (
      <>
        <p>
          POST newline-delimited JSON, one object per row, to the Events API. Field names match
          your columns; unknown fields are skipped rather than rejecting the batch.
        </p>
        <Code lang="bash">{`curl -X POST "https://api.saucer.sh/v0/events?name=hits" \\
  -H "Authorization: Bearer $TOKEN" \\
  --data-binary '{"timestamp":"2026-08-23 10:00:00","url":"/pricing","country":"DE","revenue":1.5}
{"timestamp":"2026-08-23 10:00:01","url":"/docs","country":"GB","revenue":0}'

{"successful_rows":2,"quarantined_rows":0}`}</Code>
        <p>
          Inserts are batched server-side, which is what makes high-rate ingest work at all —
          without it, a few hundred events a second becomes a few hundred parts a second and the
          merge scheduler never catches up. The cost of that bargain is that a successful
          response means <em>accepted</em>, not <em>already queryable</em>; rows become visible
          within a second or so.
        </p>
        <p>
          <code>quarantined_rows</code> counts rows that parsed as JSON but did not fit the
          table — a string where a number belongs, a malformed date. They are set aside rather
          than failing the batch, so one bad row in ten thousand does not cost you the other
          9,999. A non-zero count here is the first thing to check when a chart looks thin.
        </p>
        <p className="mdocs__note">
          Use a token scoped to <code>append</code> on this data source. A token that can write
          events should not also be able to read anything back out.
        </p>
      </>
    ),
  },
  {
    id: "opentelemetry",
    title: "OpenTelemetry",
    body: (
      <>
        <p>
          Traces, metrics and logs arrive over OTLP on the paths the specification defines. There
          is no collector to run and no exporter plugin to install — point any OpenTelemetry SDK
          at the API with a workspace token and it lands.
        </p>
        <Code lang="bash">{`export OTEL_EXPORTER_OTLP_ENDPOINT="https://api.saucer.sh"
export OTEL_EXPORTER_OTLP_HEADERS="Authorization=Bearer $TOKEN"

# that is the whole setup — your app already knows what to do with these`}</Code>
        <p>
          Both OTLP encodings work. Protobuf is what most SDKs send by default and what you get
          if you configure nothing; JSON works if your exporter is set to it. The reply comes
          back in whichever one you sent.
        </p>
        <p>
          The first export creates five tables in your workspace. From then on they are ordinary
          tables: query them from a pipe, publish an endpoint over them, join them against your
          own data.
        </p>
        <Code lang="sql">{`-- slowest endpoints in the last hour, as a published endpoint
SELECT
    SpanName,
    count()                              AS calls,
    round(quantile(0.95)(Duration) / 1e6) AS p95_ms
FROM otel_traces
WHERE ServiceName = {{String(service, 'checkout')}}
  AND Timestamp >= now() - toIntervalHour(1)
GROUP BY SpanName
ORDER BY p95_ms DESC
LIMIT {{Int64(limit, 20)}}`}</Code>
        <p>
          Column names follow the OpenTelemetry Collector&rsquo;s ClickHouse exporter, so a
          dashboard or saved query written against a self-hosted collector keeps working when
          you point it here.
        </p>
        <table className="mdocs__table">
          <thead>
            <tr>
              <th>Table</th>
              <th>Holds</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code>otel_traces</code>
              </td>
              <td>Spans, with events and links as parallel arrays</td>
            </tr>
            <tr>
              <td>
                <code>otel_logs</code>
              </td>
              <td>Log records, with a token bloom filter on the body</td>
            </tr>
            <tr>
              <td>
                <code>otel_metrics_gauge</code>
              </td>
              <td>Gauge points</td>
            </tr>
            <tr>
              <td>
                <code>otel_metrics_sum</code>
              </td>
              <td>Counters and up-down counters</td>
            </tr>
            <tr>
              <td>
                <code>otel_metrics_histogram</code>
              </td>
              <td>Explicit-bucket histograms</td>
            </tr>
          </tbody>
        </table>
        <p>
          Attributes are <code>Map(String, String)</code>, indexed on their keys. Numbers survive
          the trip — <code>toFloat64OrNull(SpanAttributes[&#39;http.status_code&#39;])</code> works
          — and trace and span ids are stored as hex, so an id copied out of any tracing UI
          matches a <code>WHERE</code> clause directly.
        </p>
        <p className="mdocs__note">
          Two things this does not do yet: exponential histograms and summary metrics are
          rejected rather than stored, and the export says so in its OTLP{" "}
          <code>partial_success</code> reply rather than dropping them quietly. Everything else in
          the same request is still accepted. Telemetry tables default to a 90-day TTL — raise it
          with <code>ALTER TABLE otel_traces MODIFY TTL …</code> if you need longer.
        </p>
      </>
    ),
  },
  {
    id: "pipes",
    title: "Pipes",
    body: (
      <>
        <p>
          A pipe is a chain of SQL nodes. Each node can select from your data sources and from
          any earlier node by name, so a hard query can be written in readable steps and each
          step inspected on its own.
        </p>
        <p>
          Nodes are inlined into a single statement when the pipe runs — they become CTEs — so
          the chain costs nothing at query time. The editor will show you the compiled SQL
          before you publish.
        </p>
        <Code lang="sql">{`-- node: recent
SELECT * FROM hits WHERE timestamp >= now() - toIntervalDay(7)

-- node: endpoint  (reads the node above)
SELECT url, count() AS hits
FROM recent
WHERE country = {{String(country, 'US')}}
GROUP BY url ORDER BY hits DESC LIMIT {{Int64(limit, 10)}}`}</Code>
        <p>
          Node names, pipe names and data source names share one rule: lowercase letters, digits
          and underscores, starting with a letter. Names must be unique within a pipe, and a
          chain that references itself in a loop is refused at save time with the cycle named,
          rather than being stored and failing later at query time.
        </p>
      </>
    ),
  },
  {
    id: "params",
    title: "Parameters",
    body: (
      <>
        <p>
          Parameters are written <code>{"{{Type(name, default)}}"}</code> and are{" "}
          <strong>typed, which is what makes them safe</strong>. A value is parsed into its
          declared type first and rendered from the parsed value, so a caller's string is never
          pasted into the statement. A parameter with no default is required.
        </p>
        <p>There is no raw or untyped parameter, and there will not be one.</p>
        <p className="mdocs__chips">
          {PARAM_TYPES.map((type) => (
            <span key={type} className="md-chip mono">
              {type}
            </span>
          ))}
        </p>
        <Code lang="sql">{`WHERE url = {{String(url)}}            -- required
  AND day >= {{Date(since, '2026-01-01')}}
  AND id IN {{Array(Int64)(ids)}}
LIMIT {{Int64(limit, 10)}}

# ?limit=10;DROP TABLE hits
{"error":"parameter \`limit\` is not a valid Int64"}`}</Code>
        <p className="mdocs__note">
          The parameter list is derived from the SQL rather than declared alongside it, so the
          two cannot drift apart.
        </p>
      </>
    ),
  },
  {
    id: "endpoints",
    title: "Publishing endpoints",
    body: (
      <>
        <p>
          Publishing a node turns it into an HTTP endpoint. It is live the moment you save, and
          unpublishing stops it just as quickly.
        </p>
        <Code lang="bash">{`curl "https://api.saucer.sh/v0/pipes/top_urls.json?country=DE&limit=5" \\
  -H "Authorization: Bearer $TOKEN"

{
  "meta": [{"name":"url","type":"String"}, …],
  "data": [{"url":"/docs","hits":"501073"}, …],
  "rows": 5,
  "statistics": {"elapsed":0.007,"rows_read":16384,"bytes_read":337275}
}`}</Code>
        <p>
          The token may also be passed as <code>?token=</code>. That exists because a browser
          calling an endpoint directly often cannot set headers without triggering a CORS
          preflight, and a preflight would double the latency of the thing you came for.
        </p>
        <p>Two response headers are worth knowing:</p>
        <Code lang="http">{`x-saucer-cache: hit | miss     was this answer served from cache
x-saucer-db-ms: 7.09           what the database itself spent`}</Code>
      </>
    ),
  },
  {
    id: "control-api",
    title: "Control API",
    body: (
      <>
        <p>
          Everything the dashboard does, it does through these routes, and so can you. They live
          on <code>saucer.sh/api</code> — the control plane — and authenticate with a session
          cookie or an account token as <code>Authorization: Bearer sc_…</code>. They manage;
          they never serve. Reading and writing your data is the other host,{" "}
          <code>api.saucer.sh</code>, documented above.
        </p>

        <h3 className="mdocs__h3">Workspaces</h3>
        <table className="mdocs__table">
          <thead>
            <tr>
              <th>Route</th>
              <th>Does</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code>POST /api/databases</code>
              </td>
              <td>
                Create. Body <code>{`{engine, tier, name}`}</code>, where engine is{" "}
                <code>postgres</code> or <code>clickhouse</code> and tier is <code>shared</code>{" "}
                or <code>dedicated</code>. Returns <code>201</code> with the password, once.
              </td>
            </tr>
            <tr>
              <td>
                <code>GET /api/databases</code>
              </td>
              <td>List everything on the account.</td>
            </tr>
            <tr>
              <td>
                <code>GET /api/databases/allowance</code>
              </td>
              <td>What the current plan permits, and how much of it is used.</td>
            </tr>
            <tr>
              <td>
                <code>POST /api/databases/:id/rotate</code>
              </td>
              <td>New password, effective immediately. The old one stops working.</td>
            </tr>
            <tr>
              <td>
                <code>DELETE /api/databases/:id</code>
              </td>
              <td>Destroy the instance and its data.</td>
            </tr>
          </tbody>
        </table>
        <p className="mdocs__note">
          Database names are lowercase letters, digits and <em>dashes</em>, starting with a
          letter. Data sources, pipes and nodes use <em>underscores</em> instead — they become
          SQL identifiers, and a dash is not one.
        </p>

        <h3 className="mdocs__h3">Data sources</h3>
        <Code lang="bash">{`GET    /api/workspaces/:id/datasources          → {datasources, ingestHost}
POST   /api/workspaces/:id/datasources          → 201 DataSource
DELETE /api/workspaces/:id/datasources/:name    → 204`}</Code>
        <p>
          The create body is <code>{`{name, columns, sortingKey, partitionBy?, ttl?}`}</code>.
          Each column is <code>{`{name, type, nullable?}`}</code>, and the type must be one of
          the closed set listed under Data sources — it is the only value interpolated into DDL
          unquoted, so it is checked rather than trusted.
        </p>
        <p>
          Deleting drops the table on the box first and the record second. The order is
          deliberate: the other way round would leave the daemon serving a data source that
          nothing knows about.
        </p>

        <h3 className="mdocs__h3">Pipes</h3>
        <Code lang="bash">{`GET    /api/workspaces/:id/pipes                → {pipes, apiHost}
PUT    /api/workspaces/:id/pipes/:name          → Pipe
DELETE /api/workspaces/:id/pipes/:name          → 204
POST   /api/workspaces/:id/pipes/:name/explain  → {sql, params}`}</Code>
        <p>
          <strong>
            The <code>PUT</code> is both save and publish
          </strong>{" "}
          — there is no separate publish call. Setting <code>endpointNode</code> to a node name
          pushes the compiled SQL to the daemon and the endpoint is live when the call returns;
          setting it to <code>null</code> takes it down just as fast. That is the single most
          important thing to know about this API.
        </p>
        <table className="mdocs__table">
          <thead>
            <tr>
              <th>Field</th>
              <th>Meaning</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code>nodes</code>
              </td>
              <td>
                At least one <code>{`{name, sql}`}</code>. SQL is capped at 64,000 characters.
              </td>
            </tr>
            <tr>
              <td>
                <code>endpointNode</code>
              </td>
              <td>
                Which node to publish, or <code>null</code> for a draft. Must name a node in
                this pipe.
              </td>
            </tr>
            <tr>
              <td>
                <code>cacheSeconds</code>
              </td>
              <td>
                Clamped to 0–3600. Defaults to 0, which is off. A cache measured in hours is a
                support ticket, which is why the ceiling exists.
              </td>
            </tr>
            <tr>
              <td>
                <code>maxThreads</code>
              </td>
              <td>
                Clamped to 0–32, defaults to 2. 0 leaves the server's own default. Above the
                box's core count only adds contention.
              </td>
            </tr>
          </tbody>
        </table>
        <p>
          <code>explain</code> compiles without saving. It is what the editor calls to show the
          CTE-flattened statement and the derived parameter list before anything is published,
          and it is the right thing to call from a test that asserts a pipe still compiles.
        </p>

        <h3 className="mdocs__h3">Tokens</h3>
        <Code lang="bash">{`GET    /api/workspaces/:id/tokens               → {tokens}
POST   /api/workspaces/:id/tokens               → 201, includes the secret once
DELETE /api/workspaces/:id/tokens/:tokenId      → 204`}</Code>
        <p>
          Creating a token is the one route here that <strong>refuses account tokens</strong> and
          requires a browser session — it answers <code>403</code> with "Create API tokens from
          the website." An <code>sc_</code> token that could mint workspace tokens would turn a
          single leaked credential into permanent data access, so that path is closed on purpose.
          Mint them in the dashboard.
        </p>
        <p>
          The body is <code>{`{name, scopes}`}</code>, each scope being{" "}
          <code>{`{type, resource}`}</code>. The returned <code>token</code> field starts with{" "}
          <code>p.</code> and is the only time the secret exists in readable form.
        </p>

        <h3 className="mdocs__h3">Stats and repair</h3>
        <Code lang="bash">{`GET  /api/workspaces/:id/stats                  → {windows}
POST /api/workspaces/:id/sync                   → {synced: true}`}</Code>
        <p>
          Every mutation above already re-pushes tokens and endpoints to the daemon, so{" "}
          <code>sync</code> is not part of a normal flow. It exists for one situation: a box that
          lost its state and needs to be told everything again. Calling it is safe and idempotent
          — the daemon's copy is a cache of the control plane's, and the cheapest correct repair
          is to replace it whole.
        </p>
      </>
    ),
  },
  {
    id: "speed",
    title: "Making it fast",
    body: (
      <>
        <p>
          Two settings per endpoint, and between them they are worth about sixty times on our
          hardware.
        </p>
        <p>
          <strong>Threads.</strong> A serving query reads little, and spends longer coordinating
          threads than reading. Capping the count is worth 2.2× on a query reading two granules.
          The default of 2 suits almost every published endpoint; raise it only if yours really
          does aggregate millions of rows.
        </p>
        <p>
          <strong>Cache.</strong> Set a lifetime in seconds and the answer is served from
          memory, taking a hot endpoint to {HEADLINE_BENCHMARK.p50} ms and its ceiling to
          {" "}
          {Math.round(HEADLINE_BENCHMARK.requestsPerSecond / 1000)}k requests a second at one
          connection. It is off by default because it is a freshness decision, not a performance
          one: a cached result can be up to its lifetime old, and only you know whether that is
          acceptable.
        </p>
        <p>
          <strong>Everything else is the sorting key.</strong> If an endpoint is slow, look at
          <code>rows_read</code> in its own response before anything else. An endpoint that got
          slow has nearly always started reading more.
        </p>
      </>
    ),
  },
  {
    id: "tokens",
    title: "Tokens and scopes",
    body: (
      <>
        <p>
          API tokens authenticate programs against your endpoints. They are separate from your
          account's CLI tokens and are scoped per resource.
        </p>
        <Code lang="bash">{`read   top_urls    may call that one endpoint, nothing else
append hits        may post events to that data source, and cannot read
admin  —           the whole workspace, including ad-hoc SQL`}</Code>
        <p>
          Scope them narrowly. A <code>read</code> token embedded in a web page should name the
          single endpoint it needs; it then cannot read a different one, cannot append, and
          cannot run SQL.
        </p>
        <p>
          A scope with a <code>null</code> resource is unscoped and covers every endpoint in the
          workspace. That is convenient for a backend you control and wrong for anything you
          ship to a browser.
        </p>
        <p className="mdocs__note">
          Revocation is immediate. Tokens are stored only as hashes, so a created token is shown
          once and never again.
        </p>
      </>
    ),
  },
  {
    id: "observability",
    title: "Watching it",
    body: (
      <>
        <p>
          Every endpoint's latency is measured where the request is answered, beside the
          database, and reported per window: requests, errors, p50, p95, p99 and the rows read.
        </p>
        <p>
          The number to watch is <code>rows_read</code> over time. Latency creeping up with it
          means the data grew; latency creeping up without it means something else. Latency
          jumping with a step change in rows read means a query stopped using its sorting key —
          most often because a filter moved off the key's leading columns.
        </p>
      </>
    ),
  },
  {
    id: "limits",
    title: "Limits and caveats",
    body: (
      <>
        <ul className="mdocs__list">
          <li>
            <strong>TLS only</strong> on both wire protocols. Plaintext is refused with a
            readable error.
          </li>
          <li>
            <strong>Endpoints are read-only.</strong> A published pipe that tries to write is
            refused by the engine.
          </li>
          <li>
            <strong>One statement per request.</strong> The query API does not accept batches.
          </li>
          <li>
            <strong>Queries are capped</strong> at 30 seconds, and each workspace has its own
            memory and execution quota so a neighbour's query cannot become your problem.
          </li>
          <li>
            <strong>Ingest is asynchronous.</strong> A 200 means accepted, not yet queryable.
          </li>
          <li>
            <strong>Ad-hoc SQL is admin-scope only</strong>, on{" "}
            <code>GET /v0/sql?q=…</code>. Published endpoints are the supported way to read.
          </li>
          <li>
            <strong>A sorting key cannot be changed</strong> after the table exists. Getting it
            wrong means creating a new data source and backfilling.
          </li>
        </ul>
      </>
    ),
  },
];
